import { Dispatch } from 'redux';
import {
  PROFILE_FETCH_REQUEST,
  PROFILE_FETCH_SUCCESS,
  PROFILE_FETCH_FAILURE,
  PROFILE_CLEAR,
} from './actionTypes';
import { apiFetch } from '../services/api';
import { RootState } from '../types';

// ─── Action interfaces ───────────────────────────────────────────

export interface ProfileFetchRequestAction { type: typeof PROFILE_FETCH_REQUEST; payload: string; environment: string; }
export interface ProfileFetchSuccessAction { type: typeof PROFILE_FETCH_SUCCESS; payload: { id: string; data: any }; }
export interface ProfileFetchFailureAction { type: typeof PROFILE_FETCH_FAILURE; payload: string; }
export interface ProfileClearAction { type: typeof PROFILE_CLEAR; }

export type ProfileAction =
  | ProfileFetchRequestAction
  | ProfileFetchSuccessAction
  | ProfileFetchFailureAction
  | ProfileClearAction;

// ─── Action creators ─────────────────────────────────────────────

export const clearProfile = (): ProfileClearAction => ({ type: PROFILE_CLEAR });

// ─── Async thunks ────────────────────────────────────────────────

export function fetchProfile(profileId: string) {
  return async (dispatch: Dispatch<ProfileAction>, getState: () => RootState) => {
    const environment = getState().environment.current;
    dispatch({ type: PROFILE_FETCH_REQUEST, payload: profileId, environment });

    try {
      const response = await apiFetch(`/api/profile/${profileId}`, { environment });

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch profile');
      }

      dispatch({
        type: PROFILE_FETCH_SUCCESS,
        payload: { id: profileId, data: response.data },
      });
    } catch (error: any) {
      dispatch({
        type: PROFILE_FETCH_FAILURE,
        payload: error.message || 'Something went wrong',
      });
    }
  };
}

// ─── Early profile request (2026-10-14) ─────────────────────────
//
// On a reload or a new tab the sign-in check (/api/auth/me) holds the page, and the
// profile request used to start only after it, so the page "blinked twice". The route
// starts the request while the check runs (routes/prefetch.ts); the page's own fetch on
// mount then takes it over instead of asking again.

/** How long an early request stays the one the page takes over. */
export const PREFETCH_REUSE_MS = 30_000;
let prefetched: { key: string; at: number; done: Promise<void> } | null = null;

export function prefetchProfile(profileId: string) {
  return (dispatch: Dispatch<any>, getState: () => RootState) => {
    const environment = getState().environment.current;
    const state = getState().profile;
    if (state.profileId === profileId && state.loading) return; // already on its way
    const done = (dispatch(fetchProfile(profileId) as any) as Promise<void>).then(() => {
      // A refused early request (the emailed code still owed, no access to the page) must not
      // stand as this profile's state: the tabs that load only when the store holds another
      // profile would never ask again.
      const after = getState().profile;
      if (after.profileId === profileId && after.error) dispatch(clearProfile());
    });
    prefetched = { key: `${environment}:${profileId}`, at: Date.now(), done };
  };
}

/** The page's load on mount: takes over an early request for the same profile, else fetches. */
export function fetchProfileOnMount(profileId: string) {
  return async (dispatch: Dispatch<any>, getState: () => RootState) => {
    const key = `${getState().environment.current}:${profileId}`;
    const early = prefetched && prefetched.key === key && Date.now() - prefetched.at < PREFETCH_REUSE_MS ? prefetched : null;
    prefetched = null;
    if (early) {
      await early.done;
      const state = getState().profile;
      if (state.profileId === profileId && state.data && !state.error) return;
    }
    await (dispatch(fetchProfile(profileId) as any) as Promise<void>);
  };
}

/** Test seam: forget an early request. */
export function resetProfilePrefetch(): void {
  prefetched = null;
}

/**
 * Maps the frontend formData (snake_case) to the backend API payload (camelCase)
 * and packs metadata fields into a single metadata JSON object.
 */
function buildUpdatePayload(formData: Record<string, any>) {
  return {
    firstName: formData.first_name,
    lastName: formData.last_name,
    email: formData.email,
    linkedin: formData.linkedin,
    phoneNumber: formData.phone_number,
    workExperienceLevel: formData.work_experience_level,
    languageProficiency: formData.language_proficiency,
    pronouns: formData.pronouns,
    headline: formData.headline,
    preferredName: formData.preferred_name,
    bio: formData.bio,
    preferredTimeCommitmentNew: Array.isArray(formData.preferred_time_commitment_new)
      ? formData.preferred_time_commitment_new
      : undefined,
    preferredWorkingDays: formData.preferred_working_days,
    preferredStartingHour: formData.preferred_starting_hour,
    preferredEndingHour: formData.preferred_ending_hour,
    addressLine1: formData.address_line1,
    addressLine2: formData.address_line2,
    addressCity: formData.address_city,
    addressCountryId: formData.address_country,
    addressCountrySubdivisionId: formData.address_country_subdivision_id,
    addressPostalCode: formData.address_postal_code,
    rolePreference: Array.isArray(formData.role_preference) ? formData.role_preference : [],
    metadata: {
      role: Array.isArray(formData.role_preference) && formData.role_preference.length > 0 ? formData.role_preference[0] : formData.role,
      overall_work_experience: formData.overall_work_experience,
      accounting_tax_experience: formData.accounting_or_tax_experience,
      us_work_experience: formData.us_work_experience,
      monthly_income: formData.monthly_income,
      comp_notes: formData.comp_notes,
      cpa: formData.cpa,
      qbo: formData.qbo,
      netsuite: formData.netsuite,
      client_communication: formData.client_communication,
      industry: formData.industry,
      notes: formData.metadata_notes,
      secondary_emails: formData.secondary_emails,
      secondary_phone_number: String(formData.secondary_phone_number ?? '').trim() || null,
    },
  };
}

export function updateTalentDetails(profileId: string, formData: Record<string, any>) {
  return async (dispatch: Dispatch<any>, getState: () => RootState) => {
    const environment = getState().environment.current;
    const payload = buildUpdatePayload(formData);

    const response = await apiFetch(`/api/profile/${profileId}/details`, {
      environment,
      method: 'PUT',
      body: payload,
    });

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to update profile');
    }

    // Re-fetch the full profile to get fresh data
    await dispatch(fetchProfile(profileId) as any);

    return response.data;
  };
}

// ─── Override stage ──────────────────────────────────────────────

export function overrideProfileStage(
  profileId: string,
  newStage: string,
  firstRoundReviewer?: string,
  /** Required justification — the backend 400s without it; shown on the
   *  Timeline's stage-override log entry. */
  note?: string,
  /** Backlog only: why they're parked (a code from GET /api/backlog/reasons). */
  backlogReason?: string,
) {
  return async (dispatch: Dispatch<any>, getState: () => RootState) => {
    const environment = getState().environment.current;
    const user = getState().user;

    const response = await apiFetch(`/api/profile/${profileId}/override-stage`, {
      environment,
      method: 'PUT',
      body: {
        newStage,
        approvedBy: user.fullName,
        ...(firstRoundReviewer !== undefined ? { firstRoundReviewer } : {}),
        ...(note ? { note } : {}),
        ...(newStage === 'backlog' && backlogReason ? { backlogReason } : {}),
      },
    });

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to override stage');
    }

    // Re-fetch the full profile to get fresh data
    await dispatch(fetchProfile(profileId) as any);

    return response.data;
  };
}

// ─── Interview actions (reusable from any page) ─────────────────

export interface ApproveTechnicalParams {
  accountManagerId?: string;
  firstQuestionId?: string;
  secondQuestionId?: string;
  firstQuestionLabel?: string;
  secondQuestionLabel?: string;
  communication?: string;
  score?: string;
  monthlyIncome?: string | number;
  monthlyIncomeCurrency?: string;
  monthlyIncomeOriginal?: string | number;
  monthlyIncomeFxRate?: number;
  monthlyIncomeFxAsOf?: string;
  monthlyIncomePeriod?: string;
  compNotes?: string;
  feedbackHtml?: string;
  recordingUrl?: string;
  approveReason?: string;
  fastTrack?: boolean;
  /** Work experience level (L1-L7) — shared profile field, mirrors behavioral. */
  workExperienceLevel?: string;
  /** ISO datetime from the missing-record retry dialog — when the
   *  interview actually happened (no Calendly booking was found). */
  interviewHappenedAt?: string;
  /** Skip from the same dialog — proceed WITHOUT recording a call. */
  skipInterviewRecord?: boolean;
  /** Urgent-hiring AI candidates: whose final-round Calendly the email carries (the approve dialog's picker). */
  urgentFinalRoundInterviewerEmail?: string;
  talent: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    stage: string;
    account_manager: string | null;
    interviews: any[];
  };
}

export function approveTechnicalInterview(profileId: string, params: ApproveTechnicalParams) {
  return async (dispatch: Dispatch<any>, getState: () => RootState) => {
    const environment = getState().environment.current;
    const user = getState().user;

    const response = await apiFetch(`/api/profile/${profileId}/interviews/approve-technical`, {
      environment,
      method: 'POST',
      body: { ...params, approvedBy: user.fullName },
    });

    if (!response.success) {
      // Machine-readable code survives (MISSING_INTERVIEW_RECORD drives the
      // off-Calendly date/time retry dialog — see MissingInterviewRecordDialog).
      const err: any = new Error(response.error?.message || 'Failed to approve technical interview');
      err.code = response.error?.code;
      throw err;
    }

    // Re-fetch the full profile to get fresh data
    await dispatch(fetchProfile(profileId) as any);

    return response.data;
  };
}

// ─── Behavioral interview approve ───────────────────────────────

export interface ApproveBehavioralParams {
  accountManagerId?: string;
  communication?: string;
  score?: string;
  feedbackHtml?: string;
  workExperienceLevel?: string;
  behavioralSkillScore?: string;
  behavioralResourcefulnessScore?: string;
  tags?: string[];
  recordingUrl?: string;
  monthlyIncome?: string | number;
  monthlyIncomeCurrency?: string;
  monthlyIncomeOriginal?: string | number;
  monthlyIncomeFxRate?: number;
  monthlyIncomeFxAsOf?: string;
  monthlyIncomePeriod?: string;
  compNotes?: string;
  /** ISO datetime from the missing-record retry dialog — when the
   *  interview actually happened (no Calendly booking was found). */
  interviewHappenedAt?: string;
  /** Skip from the same dialog — proceed WITHOUT recording a call. */
  skipInterviewRecord?: boolean;
  talent: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    stage: string;
    account_manager: string | null;
    interviews: any[];
  };
}

export function approveBehavioralInterview(profileId: string, params: ApproveBehavioralParams) {
  return async (dispatch: Dispatch<any>, getState: () => RootState) => {
    const environment = getState().environment.current;
    const user = getState().user;

    const response = await apiFetch(`/api/profile/${profileId}/interviews/approve-behavioral`, {
      environment,
      method: 'POST',
      body: { ...params, approvedBy: user.fullName },
    });

    if (!response.success) {
      // Machine-readable code survives (MISSING_INTERVIEW_RECORD drives the
      // off-Calendly date/time retry dialog — see MissingInterviewRecordDialog).
      const err: any = new Error(response.error?.message || 'Failed to approve behavioral interview');
      err.code = response.error?.code;
      throw err;
    }

    await dispatch(fetchProfile(profileId) as any);

    return response.data;
  };
}

// ─── Technical interview reject ─────────────────────────────────

export interface RejectTechnicalParams {
  accountManagerId?: string;
  firstQuestionId?: string;
  secondQuestionId?: string;
  firstQuestionLabel?: string;
  secondQuestionLabel?: string;
  communication?: string;
  score?: string;
  monthlyIncome?: string | number;
  monthlyIncomeCurrency?: string;
  monthlyIncomeOriginal?: string | number;
  monthlyIncomeFxRate?: number;
  monthlyIncomeFxAsOf?: string;
  monthlyIncomePeriod?: string;
  compNotes?: string;
  feedbackHtml?: string;
  recordingUrl?: string;
  /** Work experience level (L1-L7) — shared profile field, mirrors behavioral. */
  workExperienceLevel?: number | null;
  rejectChoice: 'inactive' | 'cooldown';
  rejectReasons: string[];
  rejectOther?: string;
  cooldownUntil?: string;
  /** ISO datetime from the missing-record retry dialog — when the
   *  interview actually happened (no Calendly booking was found). */
  interviewHappenedAt?: string;
  /** Skip from the same dialog — proceed WITHOUT recording a call. */
  skipInterviewRecord?: boolean;
  talent: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    stage: string;
    account_manager: string | null;
    interviews: any[];
    rejected_count: number | null;
  };
}

export function rejectTechnicalInterview(profileId: string, params: RejectTechnicalParams) {
  return async (dispatch: Dispatch<any>, getState: () => RootState) => {
    const environment = getState().environment.current;
    const user = getState().user;

    const response = await apiFetch(`/api/profile/${profileId}/interviews/reject-technical`, {
      environment,
      method: 'POST',
      body: { ...params, approvedBy: user.fullName },
    });

    if (!response.success) {
      // Machine-readable code survives (MISSING_INTERVIEW_RECORD drives the
      // off-Calendly date/time retry dialog — see MissingInterviewRecordDialog).
      const err: any = new Error(response.error?.message || 'Failed to reject technical interview');
      err.code = response.error?.code;
      throw err;
    }

    await dispatch(fetchProfile(profileId) as any);

    return response.data;
  };
}

// ─── Behavioral interview reject ────────────────────────────────

export interface RejectBehavioralParams {
  accountManagerId?: string;
  communication?: string;
  score?: string;
  feedbackHtml?: string;
  workExperienceLevel?: number | null;
  behavioralSkillScore?: number | null;
  behavioralResourcefulnessScore?: number | null;
  tags?: string[];
  recordingUrl?: string;
  monthlyIncome?: string | number;
  monthlyIncomeCurrency?: string;
  monthlyIncomeOriginal?: string | number;
  monthlyIncomeFxRate?: number;
  monthlyIncomeFxAsOf?: string;
  monthlyIncomePeriod?: string;
  compNotes?: string;
  rejectChoice: 'inactive' | 'cooldown';
  rejectReasons: string[];
  rejectOther?: string;
  cooldownUntil?: string;
  /** ISO datetime from the missing-record retry dialog — when the
   *  interview actually happened (no Calendly booking was found). */
  interviewHappenedAt?: string;
  /** Skip from the same dialog — proceed WITHOUT recording a call. */
  skipInterviewRecord?: boolean;
  talent: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    stage: string;
    account_manager: string | null;
    interviews: any[];
    rejected_count: number | null;
  };
}

export function rejectBehavioralInterview(profileId: string, params: RejectBehavioralParams) {
  return async (dispatch: Dispatch<any>, getState: () => RootState) => {
    const environment = getState().environment.current;
    const user = getState().user;

    const response = await apiFetch(`/api/profile/${profileId}/interviews/reject-behavioral`, {
      environment,
      method: 'POST',
      body: { ...params, approvedBy: user.fullName },
    });

    if (!response.success) {
      // Machine-readable code survives (MISSING_INTERVIEW_RECORD drives the
      // off-Calendly date/time retry dialog — see MissingInterviewRecordDialog).
      const err: any = new Error(response.error?.message || 'Failed to reject behavioral interview');
      err.code = response.error?.code;
      throw err;
    }

    await dispatch(fetchProfile(profileId) as any);

    return response.data;
  };
}

// ─── No-show follow-up ──────────────────────────────────────────

export interface NoShowFollowUpParams {
  accountManagerId?: string;
  feedbackHtml?: string;
  // Explicit interview round from the round-specific UI. Correct for follow-up
  // no-shows (where stage no longer encodes the round). Server falls back to
  // stage derivation when omitted.
  interviewType?: 'technical' | 'behavioral';
  // No-show action flow: which downstream path the interviewer chose.
  // Omitted defaults to 'reschedule' (legacy behavior) on the server.
  // 'reschedule_no_email' = the reschedule flow (no-show + follow-up) but no email sent.
  action?: 'ai' | 'reschedule' | 'reschedule_no_email' | 'reject';
  // Optional internal note captured in the no-show action modal.
  internalNote?: string;
  talent: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    stage: string;
    account_manager: string | null;
    interviews: any[];
    rejected_count?: number | null;
  };
}

export function noShowFollowUpInterview(profileId: string, params: NoShowFollowUpParams) {
  return async (dispatch: Dispatch<any>, getState: () => RootState) => {
    const environment = getState().environment.current;
    const user = getState().user;

    const response = await apiFetch(`/api/profile/${profileId}/interviews/no-show`, {
      environment,
      method: 'POST',
      body: { ...params, approvedBy: user.fullName },
    });

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to process no-show follow-up');
    }

    await dispatch(fetchProfile(profileId) as any);

    return response.data;
  };
}

// ─── Edit technical interview ───────────────────────────────────

export interface EditTechnicalInterviewParams {
  interviewId?: string;
  attemptNumber?: number;
  accountManagerId?: string;
  firstQuestionId?: string;
  secondQuestionId?: string;
  firstQuestionAttachmentId?: string;
  secondQuestionAttachmentId?: string;
  communication?: string;
  score?: string;
  /** Work experience level (L1-L7) — shared profile field, mirrors behavioral. */
  workExperienceLevel?: number | null;
  monthlyIncome?: string | number;
  monthlyIncomeCurrency?: string;
  monthlyIncomeOriginal?: string | number;
  monthlyIncomeFxRate?: number;
  monthlyIncomeFxAsOf?: string;
  monthlyIncomePeriod?: string;
  compNotes?: string;
  feedbackHtml?: string;
  recordingUrl?: string;
}

export function editTechnicalInterview(profileId: string, params: EditTechnicalInterviewParams) {
  return async (dispatch: Dispatch<any>, getState: () => RootState) => {
    const environment = getState().environment.current;

    const response = await apiFetch(`/api/profile/${profileId}/interviews/edit-technical`, {
      environment,
      method: 'PUT',
      body: params,
    });

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to update technical interview');
    }

    await dispatch(fetchProfile(profileId) as any);

    return response.data;
  };
}

// ─── Edit behavioral interview ──────────────────────────────────

export interface EditBehavioralInterviewParams {
  interviewId?: string;
  attemptNumber?: number;
  accountManagerId?: string;
  communication?: string;
  score?: string;
  feedbackHtml?: string;
  workExperienceLevel?: number | null;
  behavioralSkillScore?: number | null;
  behavioralResourcefulnessScore?: number | null;
  tags?: string[];
  recordingUrl?: string;
  rolePreference?: string[];
  monthlyIncome?: string | number;
  monthlyIncomeCurrency?: string;
  monthlyIncomeOriginal?: string | number;
  monthlyIncomeFxRate?: number;
  monthlyIncomeFxAsOf?: string;
  monthlyIncomePeriod?: string;
  compNotes?: string;
}

export function editBehavioralInterview(profileId: string, params: EditBehavioralInterviewParams) {
  return async (dispatch: Dispatch<any>, getState: () => RootState) => {
    const environment = getState().environment.current;

    const response = await apiFetch(`/api/profile/${profileId}/interviews/edit-behavioral`, {
      environment,
      method: 'PUT',
      body: params,
    });

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to update behavioral interview');
    }

    await dispatch(fetchProfile(profileId) as any);

    return response.data;
  };
}

// ─── Revet interviews (a list per talent — server/src/utils/revets.ts) ──

export interface RevetInterviewParams {
  tags: string[];
  recordingUrl: string | null;
  feedbackHtml: string | null;
  interviewerEmail: string | null;
  interviewerName: string | null;
}

/** Runs one re-vet request, then refetches the profile. Throws the API error (with its code). */
function revetRequest(profileId: string, path: string, method: 'POST' | 'PUT' | 'DELETE', body?: unknown) {
  return async (dispatch: Dispatch<any>, getState: () => RootState) => {
    const environment = getState().environment.current;
    const response = await apiFetch(`/api/profile/${profileId}/interviews/${path}`, { environment, method, body });
    await dispatch(fetchProfile(profileId) as any);
    return response.data;
  };
}

/** POST: a new re-vet. Resolves to { entry }. */
export function addRevetInterview(profileId: string, params: RevetInterviewParams) {
  return revetRequest(profileId, 'revets', 'POST', params);
}

/**
 * PUT: edit one re-vet. `expectedUpdatedAt` is the updated_at the panel LOADED
 * (gotcha #30), so a save over someone else's newer save is refused (409
 * REVET_CONFLICT). Resolves to { entry }.
 */
export function updateRevetInterview(
  profileId: string,
  revetId: string,
  params: RevetInterviewParams & { expectedUpdatedAt: string | null },
) {
  return revetRequest(profileId, `revets/${revetId}`, 'PUT', params);
}

/** DELETE: removes one re-vet (its Timeline card stays). */
export function removeRevetInterview(profileId: string, revetId: string) {
  return revetRequest(profileId, `revets/${revetId}`, 'DELETE');
}

// ─── Work Experience ──────────────────────────────────────────────

export interface AddWorkExperienceParams {
  profileId: string;
  companyId: string;
  companyName: string;
  position: string;
  location: string;
  dateStart: string;
  dateEnd: string;
  description: string;
}

export function addWorkExperience(params: AddWorkExperienceParams) {
  return async (dispatch: Dispatch<any>, getState: () => RootState) => {
    const environment = getState().environment.current;

    const response = await apiFetch(`/api/profile/work-experience`, {
      environment,
      method: 'POST',
      body: params,
    });

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to add work experience');
    }

    await dispatch(fetchProfile(params.profileId) as any);

    return response.data;
  };
}

export interface UpdateWorkExperienceParams {
  profileId: string;
  workExperienceId: string;
  companyId: string;
  companyName: string;
  position: string;
  dateStart: string;
  dateEnd: string;
  description: string;
}

export function updateWorkExperience(params: UpdateWorkExperienceParams) {
  return async (dispatch: Dispatch<any>, getState: () => RootState) => {
    const environment = getState().environment.current;

    const response = await apiFetch(`/api/profile/work-experience/${params.workExperienceId}`, {
      environment,
      method: 'PUT',
      body: {
        companyId: params.companyId,
        companyName: params.companyName,
        position: params.position,
        dateStart: params.dateStart,
        dateEnd: params.dateEnd,
        description: params.description,
      },
    });

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to update work experience');
    }

    await dispatch(fetchProfile(params.profileId) as any);

    return response.data;
  };
}
